import { useEffect, useState } from "react";
import Select from "../../../../../components/form/Select";
import JobStatus from "../../../base/models/JobStatus";
import { getUser, hasRole, canCloseJob, openCloseJob, openEditJobAppt, openInvalidCloseJob, openReopenJob, useHub } from "../../../../../core/Global";
import { API_ROUTES } from "../../../../../core/Constants";
import Job from "../../models/Job";
import TextArea from "../../../../../components/form/TextArea";
import FlexRow from "../../../../../components/base/FlexRow";
import Icon from "../../../../../components/base/Icon";
import Timer from "../../../../../components/timer/Timer";
import IconButton from "../../../../../components/base/IconButton";
import Button from "../../../../../components/base/Button";
import { PaymentTypes, UserTypes } from "../../../../../core/Enums";
import JobCancelReasonField, { CancelReasonValue, isCancelReasonValid, showCancelReasonError } from "../jobmodals/JobCancelReasonField";



interface IProps {
    job: Job
}
export default function JobDrawerStatus(props: IProps) {

    const hub = useHub()
    const user = getUser()
    const [statuses, statusesSet] = useState<JobStatus[]>([])
    const [working, workingSet] = useState<boolean>(false)
    const [selectedStatus, selectedStatusSet] = useState<JobStatus>();
    const [statusNote, statusNoteSet] = useState<string>();
    // Only used when the status being picked is a cancellation: a ready-made reason or a note
    // written here. One of the two, never both.
    const [cancelReason, cancelReasonSet] = useState<CancelReasonValue>();

    const [closeJobWorking, closeJobWorkingSet] = useState<boolean>(false)

    // Fetch statuses directly from the API so this drawer never depends on the
    // socket-fed global state being populated (and re-renders when they arrive).
    useEffect(() => {
        hub.Get(API_ROUTES.JobStatuses + "all", res => {
            if (res.success && res.data) statusesSet((res.data as JobStatus[]).filter(f => !f.isClosed))
        })
    }, [])


    // Prefer the full status from the loaded list, but fall back to the status
    // embedded on the job itself so the current status is pre-selected and its
    // circle is colored even before (or without) the statuses list loading.
    // Re-runs when the list arrives (statuses.length flips 0 -> N).
    useEffect(() => {
        if (props.job) {
            var st = statuses?.find(f => f.id == props.job.jobStatusId) ?? props.job.jobStatus;
            selectedStatusSet(st)
        }
    }, [props.job, statuses.length])

    const [refreshing, refreshingSet] = useState<boolean>(false)

    useEffect(() => {
        refreshingSet(false)
    }, [props.job])

    // Declared after the hooks (old CRA eslint reads a const between hooks as a hooks violation).
    // True while the picked status would cancel the job, which is what turns the note field into
    // the cancel-reason picker.
    const canceling = (selectedStatus?.isCanceled ?? false) && props.job.jobStatusId != selectedStatus?.id

    return (
        <div id="div-quickjob-status">

            {
                props.job.isClosed ?
                    <FlexRow>
                        <Icon icon="circle" type="solid" color={props.job.jobStatus?.color} size={16} />
                        <span className="ms-2 fs-6">
                            Job is Closed!
                        </span>
                        {
                            (!props.job.isArchived || hasRole("EditArchivedJobs")) && !user?.isTech &&
                            <button type="button" className="btn btn-primary btn-sm ms-auto"
                                onClick={e => {
                                    e.preventDefault()
                                    if (statuses) openReopenJob(props.job, statuses[0])
                                }}>
                                Reopen the Job
                            </button>
                        }

                    </FlexRow>
                    : props.job.isCanceled ?
                        <FlexRow>
                            <Icon icon="circle" type="solid" color={props.job.jobStatus?.color} size={16} />
                            <span className="ms-2 fs-6">
                                Job is Canceled!
                            </span>
                            {
                                (!props.job.isArchived || hasRole("EditArchivedJobs")) && !user?.isTech &&
                                <button type="button" className="btn btn-primary btn-sm ms-auto"
                                    onClick={e => {
                                        e.preventDefault()
                                        if (statuses) openReopenJob(props.job, statuses[0])
                                    }}>
                                    Reopen the Job
                                </button>
                            }

                        </FlexRow>
                        :
                        <div>
                            {
                                user?.userType == UserTypes.Technician ?
                                    <>
                                        <FlexRow>
                                            <Icon icon="circle" type="solid" color={selectedStatus?.color} />
                                            <div className="ms-2">
                                                {props.job.jobStatus?.name}
                                            </div>
                                            <FlexRow className="ms-auto">
                                                <Icon icon="clock" className="me-1" />
                                                <div style={{ width: 60 }}>
                                                    <Timer key={props.job.id} baseDate={props.job.lastStatusUpdatedAt} short />
                                                </div>

                                            </FlexRow>
                                        </FlexRow>
                                    </>
                                    :
                                    <>
                                        {
                                            statuses && selectedStatus &&
                                            <Select id="job-quickform-statuses" label="Status" name="status" deselect={false}
                                                labelUp block icon="circle" iconColor={selectedStatus?.color} labelClass="fw-bold"
                                                source={statuses} marginBottom={3} value={selectedStatus?.id}
                                                onSelectItem={st => selectedStatusSet(st)} />
                                        }

                                        {
                                            // A cancellation always asks why, whether or not the
                                            // status is marked note-required, and offers the
                                            // account's ready-made reasons first.
                                            canceling ?
                                                <JobCancelReasonField onChange={cancelReasonSet} />
                                                : selectedStatus?.noteRequired && props.job.jobStatusId != selectedStatus.id ?
                                                    <TextArea label="Status Note*" name="statusNote" labelUp block
                                                        onChange={e => statusNoteSet(e.target.value)} />
                                                    : <></>
                                        }

                                        {
                                            (selectedStatus && selectedStatus.id != props.job.jobStatusId) ?
                                                <FlexRow justify="end">
                                                    <button className="btn btn-secondary btn-sm me-2" disabled={working}
                                                        onClick={e => {
                                                            var st = statuses?.find(f => f.id == props.job.jobStatusId);
                                                            selectedStatusSet(st)
                                                        }}>
                                                        Discard
                                                    </button>
                                                    <button className="btn btn-success btn-sm" disabled={working}
                                                        onClick={e => {
                                                            e.preventDefault()

                                                            if (canceling && !isCancelReasonValid(cancelReason)) {
                                                                showCancelReasonError(cancelReason, "div-quickjob-status");
                                                                return;
                                                            }

                                                            if (!canceling && props.job.jobStatusId != selectedStatus?.id && selectedStatus?.noteRequired && (!statusNote || statusNote.length < 3)) {
                                                                window.showFormError("statusNote", "Please write the status note!", false, "div-quickjob-status");
                                                                return;
                                                            }

                                                            if (selectedStatus?.isAppointment) {
                                                                openEditJobAppt(props.job)
                                                                return;
                                                            }

                                                            workingSet(true)

                                                            hub.Post(API_ROUTES.Jobs + "jobstatus", {
                                                                id: props.job.id,
                                                                status: selectedStatus.id,
                                                                note: canceling ? cancelReason?.note : statusNote,
                                                                cancelReasonId: canceling ? cancelReason?.cancelReasonId : undefined

                                                            }, res => {
                                                                workingSet(false)
                                                            })

                                                        }}>
                                                        {
                                                            working ?
                                                                <>
                                                                    <Icon icon="spinner-third" spin />
                                                                    <span className="ms-2">Updating...</span>
                                                                </>
                                                                :
                                                                <>
                                                                    Update Status
                                                                </>
                                                        }
                                                    </button>
                                                </FlexRow>
                                                :
                                                <FlexRow>
                                                    <div className="text-muted fs-8">
                                                        <Icon icon="clock" size={10} className="me-2" type="solid" />
                                                        Status Time
                                                    </div>
                                                    <FlexRow className="ps-5">
                                                        <div className="fs-8" style={{ whiteSpace: "nowrap" }}>
                                                            <Timer key={props.job.id} baseDate={props.job.lastStatusUpdatedAt} short />
                                                        </div>
                                                        <IconButton icon="redo" className="ms-3 btn-light" working={refreshing}
                                                            type="solid" size={12} title="Refresh Status Time"
                                                            onClick={e => {
                                                                refreshingSet(true)
                                                                hub.Post(API_ROUTES.Jobs + "refreshstatus", {
                                                                    id: props.job.id
                                                                }, res => {

                                                                });
                                                            }} />
                                                    </FlexRow>
                                                    {
                                                        canCloseJob() &&
                                                        <div className="ms-auto">
                                                            <Button text="Close The Job" color="secondary" isRunning={closeJobWorking}
                                                                onClick={() => {
                                                                    closeJobWorkingSet(true)
                                                                    hub.Get(API_ROUTES.Jobs + "validateclosing/" + props.job.id, res => {
                                                                        if (res.data) {
                                                                            openCloseJob(props.job)
                                                                            closeJobWorkingSet(false)
                                                                        }
                                                                        else {
                                                                            openInvalidCloseJob(props.job, res.message)
                                                                            closeJobWorkingSet(false)
                                                                        }
                                                                    })
                                                                }} />
                                                        </div>
                                                    }
                                                </FlexRow>
                                        }
                                    </>
                            }

                        </div>
            }



        </div>
    )
}